/**
 * Mock data for the Design Lab preview ONLY. Never imported by real pages.
 * Real pages pass live data through the Layer 3 adapters instead.
 */
import {
  LayoutDashboard,
  CheckSquare,
  Users,
  FileText,
  Phone,
  Settings,
  Inbox,
  CreditCard,
  Send,
  Sparkles,
  ListChecks,
  CalendarClock,
} from 'lucide-react';
import type { ModernRailItem } from '../shell/ModernLeftRail';
import type { ModernNavGroup } from '../shell/ModernAppShell';
import type { ModernColumn } from '../primitives/ModernDataTableShell';
import type { AliceMessage, AliceSuggestion, AliceActionButton } from '../assistant/AliceDrawer';

export const mockRailItems: ModernRailItem[] = [
  { id: 'command-center', label: 'Command Center', icon: <LayoutDashboard className="h-5 w-5" /> },
  { id: 'tasks', label: 'Tasks', icon: <CheckSquare className="h-5 w-5" />, badge: 6 },
  { id: 'clients', label: 'Clients', icon: <Users className="h-5 w-5" /> },
  { id: 'proposals', label: 'Proposals', icon: <FileText className="h-5 w-5" /> },
  { id: 'calls', label: 'Calls', icon: <Phone className="h-5 w-5" /> },
  { id: 'settings', label: 'Settings', icon: <Settings className="h-5 w-5" /> },
];

export const mockNavGroups: ModernNavGroup[] = [
  {
    id: 'work',
    label: 'Work',
    items: [
      { id: 'today', label: 'Today', icon: <CalendarClock className="h-4 w-4" /> },
      { id: 'tasks', label: 'All Tasks', icon: <ListChecks className="h-4 w-4" /> },
      { id: 'inbox', label: 'Inbox', icon: <Inbox className="h-4 w-4" /> },
    ],
  },
  {
    id: 'clients',
    label: 'Clients',
    items: [
      { id: 'directory', label: 'Directory', icon: <Users className="h-4 w-4" /> },
      { id: 'proposals', label: 'Proposals', icon: <FileText className="h-4 w-4" /> },
      { id: 'billing', label: 'Billing', icon: <CreditCard className="h-4 w-4" /> },
    ],
  },
];

export interface MockTask {
  id: string;
  name: string;
  client: string;
  status: 'In progress' | 'Waiting on client' | 'Done' | 'Blocked';
  due: string;
  owner: string;
}

export const mockTaskColumns: ModernColumn[] = [
  { key: 'name', header: 'Task', width: 'minmax(0, 2.2fr)' },
  { key: 'client', header: 'Client', width: 'minmax(0, 1fr)', hideOnMobile: true },
  { key: 'status', header: 'Status', width: '11rem' },
  { key: 'owner', header: 'Owner', width: '8rem', hideOnMobile: true },
  { key: 'due', header: 'Due', width: '6rem', align: 'right' },
];

export const mockTasks: MockTask[] = [
  { id: 't1', name: 'Publish June newsletter', client: 'Northwind Co', status: 'In progress', due: 'Jun 28', owner: 'Dakota' },
  { id: 't2', name: 'Review homepage copy', client: 'Acme Dental', status: 'Waiting on client', due: 'Jun 30', owner: 'Priya' },
  { id: 't3', name: 'Launch Google Ads pilot', client: 'Lakeside Realty', status: 'Blocked', due: 'Jul 1', owner: 'Sam' },
  { id: 't4', name: 'Quarterly SEO report', client: 'Northwind Co', status: 'Done', due: 'Jun 24', owner: 'Dakota' },
  { id: 't5', name: 'Set up call tracking', client: 'Bright Smile', status: 'In progress', due: 'Jul 3', owner: 'Priya' },
];

export const mockStatusTone: Record<MockTask['status'], 'info' | 'warning' | 'success' | 'danger'> = {
  'In progress': 'info',
  'Waiting on client': 'warning',
  Done: 'success',
  Blocked: 'danger',
};

export interface MockTile {
  id: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
}

export const mockTiles: MockTile[] = [
  { id: 'capture', label: 'Quick Capture', hint: 'Log a task or note', icon: <CheckSquare className="h-5 w-5" /> },
  { id: 'proposal', label: 'New Proposal', hint: 'Draft for a client', icon: <FileText className="h-5 w-5" /> },
  { id: 'update', label: 'Send Update', hint: 'Client status email', icon: <Send className="h-5 w-5" /> },
  { id: 'ask', label: 'Ask Alice', hint: 'Summarize my day', icon: <Sparkles className="h-5 w-5" /> },
];

export const mockAliceMessages: AliceMessage[] = [
  { id: 'm1', role: 'user', content: 'What still needs my attention today?' },
  {
    id: 'm2',
    role: 'assistant',
    content:
      'Two things: Lakeside Realty’s ad pilot is blocked on budget approval, and Acme Dental’s homepage copy has been waiting on the client for 3 days. Want me to draft a nudge for Acme?',
  },
];

export const mockAliceSuggestions: AliceSuggestion[] = [
  { id: 's1', label: 'Summarize today', icon: <Sparkles className="h-3.5 w-3.5" /> },
  { id: 's2', label: 'Draft client update', icon: <Send className="h-3.5 w-3.5" /> },
  { id: 's3', label: 'What’s overdue?', icon: <CalendarClock className="h-3.5 w-3.5" /> },
];

export const mockAliceActions: AliceActionButton[] = [
  { id: 'a1', label: 'Draft nudge to Acme', icon: <Send className="h-3.5 w-3.5" />, tone: 'accent' },
  { id: 'a2', label: 'Mark task done', icon: <CheckSquare className="h-3.5 w-3.5" /> },
];
